import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function AboutPage() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypotValue = (formData.get('website_alt') as string || '').trim();
    if (honeypotValue) {
      setFormSubmitted(true);
      setFormLoading(false);
      return;
    }

    formData.delete('website_alt');

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormSubmitted(true);
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后再试';
        setFormError(serverMsg);
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
    } finally {
      setFormLoading(false);
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '泷泽萝拉官方资料站',
    url: 'https://jtdaifa.cn/',
    description: '专注于提供泷泽萝拉完整个人资料、作品信息与最新动态的权威参考网站。',
    foundingDate: '2024',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@takizawa-info.com',
      availableLanguage: ['Japanese', 'Chinese'],
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">
        {JSON.stringify(orgSchema)}
      </script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="max-w-[1280px] mx-auto px-4 md:px-6 pt-24 pb-16">
          <Breadcrumbs
            items={[
              { label: '首页', to: '/' },
              { label: '关于本站' },
            ]}
          />

          {/* Page Header */}
          <header className="mb-10">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
              关于本站
            </h1>
            <p className="text-foreground-600 mt-2 text-sm md:text-base">
              了解泷泽萝拉官方资料站的使命、价值观与联系方式
            </p>
          </header>

          {/* Brand Introduction */}
          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
              品牌介绍
            </h2>
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8">
              <p className="text-foreground-700 leading-relaxed mb-4">
                泷泽萝拉官方资料站成立于2024年，是专注于为泷泽萝拉粉丝提供最全面、最权威信息的参考平台。我们致力于汇集泷泽萝拉从出道至今的全部个人资料、作品信息、写真集及最新动态，成为全球泷泽萝拉粉丝的一站式资讯中心。
              </p>
              <p className="text-foreground-700 leading-relaxed mb-4">
                我们的编辑团队由资深的日本娱乐资讯从业者组成，对泷泽萝拉的职业生涯有着深入的研究与了解。我们坚持内容的准确性、时效性与全面性，确保每一条信息的来源可靠。
              </p>
              <p className="text-foreground-700 leading-relaxed">
                本站以粉丝为核心，持续更新泷泽萝拉的最新动态，包括公开活动、社交媒体发布、媒体报道等。无论您是新粉丝还是多年追随者，都能在这里找到您想了解的内容。
              </p>
            </div>
          </section>

          {/* Reputation & Values */}
          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-4">
              声誉与价值观
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                  <i className="ri-shield-check-line text-xl" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2">内容权威</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们坚持信息准确性与来源可追溯，每一条资料都经过多方核实，确保为粉丝提供最可靠的内容。
                </p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                  <i className="ri-timer-flash-line text-xl" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2">及时更新</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  我们跟进泷泽萝拉的最新动态，确保网站内容与时俱进，让粉丝第一时间获取最新资讯。
                </p>
              </div>
              <div className="bg-background-50 border border-background-200/70 rounded-xl p-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                  <i className="ri-heart-line text-xl" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2">粉丝为本</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  一切以泷泽萝拉粉丝的需求出发，持续优化网站体验，提供最丰富全面的资讯服务。
                </p>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-14">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
              常见问题
            </h2>
            <div className="space-y-3">
              {[
                {
                  q: '本站是泷泽萝拉的官方网站吗？',
                  a: '本站是非官方粉丝资讯网站，由资深娱乐资讯团队运营，旨在为粉丝提供泷泽萝拉的全方位信息参考。本站与泷泽萝拉本人及其经纪公司无直接关联。',
                },
                {
                  q: '网站信息多久更新一次？',
                  a: '我们的编辑团队每日关注泷泽萝拉的动态，一旦有新的公开活动、社交媒体更新或媒体报道，会在24小时内更新到网站上。',
                },
                {
                  q: '如何联系网站运营团队？',
                  a: '您可以通过本页下方的联系表单或发送邮件至 contact@takizawa-info.com 与我们取得联系，我们通常会在48小时内回复。',
                },
                {
                  q: '本网站是否提供泷泽萝拉写真集的下载？',
                  a: '本站仅提供泷泽萝拉写真集的信息介绍和购买指引，不提供任何盗版下载链接。我们尊重知识产权，鼓励粉丝通过正规渠道支持泷泽萝拉。',
                },
              ].map((faq, index) => (
                <details key={index} className="group bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-background-100 transition-colors">
                    <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">{faq.q}</span>
                    <i className="ri-arrow-down-s-line text-foreground-500 group-open:rotate-180 transition-transform flex-shrink-0" />
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* Contact Form */}
          <section>
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
              联系我们
            </h2>
            <div className="bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8 max-w-2xl">
              {formSubmitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className="ri-check-line text-2xl" />
                  </div>
                  <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-2">
                    感谢您的留言！
                  </h3>
                  <p className="text-sm text-foreground-600">
                    我们已收到您的信息，通常会在48小时内回复。
                  </p>
                  <button
                    onClick={() => setFormSubmitted(false)}
                    className="mt-4 text-sm text-primary-600 hover:text-primary-700 font-label cursor-pointer"
                  >
                    发送新消息
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  action="https://jtdaifa.cn/api/form/d9rhb5fnahb7382ovmk0"
                  method="POST"
                  className="space-y-5"
                  data-readdy-form
                  id="contact-form"
                >
                  {formError && (
                    <div className="p-3 rounded-lg bg-primary-50 text-primary-700 text-sm">
                      {formError}
                    </div>
                  )}

                  <div>
                    <label htmlFor="name" className="block text-sm font-label font-medium text-foreground-800 mb-1.5">
                      您的姓名
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-label font-medium text-foreground-800 mb-1.5">
                      电子邮箱
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-label font-medium text-foreground-800 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="请输入主题"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-label font-medium text-foreground-800 mb-1.5">
                      留言内容
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                      placeholder="请输入您的留言（最多500字）"
                    />
                    <p className="text-xs text-foreground-500 mt-1">最多500个字符</p>
                  </div>

                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <button
                    type="submit"
                    disabled={formLoading}
                    className={`px-6 py-2.5 bg-primary-500 text-white rounded-full text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap ${
                      formLoading ? 'opacity-60 cursor-not-allowed' : ''
                    }`}
                  >
                    {formLoading ? '发送中...' : '发送留言'}
                  </button>
                </form>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}